<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute top-1/2 left-1/4 w-96 h-96 bg-accent-yellow/5 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative max-w-4xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-10">
        <div
          class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 via-accent-red to-accent-yellow mb-4 shadow-lg shadow-primary-500/25"
        >
          <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </div>
        <h1 class="font-display text-3xl font-bold text-white mb-2">
          Активировать пробный период
        </h1>
        <p class="text-gray-400 text-lg">
          Получите <span class="text-primary-500 font-semibold">14 дней</span> полного доступа
          к выбранному тарифу
        </p>
      </div>

      <!-- Already used trial message -->
      <div v-if="!canActivate && !loading" class="card p-8 text-center">
        <div
          class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-800 mb-4"
        >
          <svg class="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 class="text-xl font-bold text-white mb-2">Пробный период уже использован</h2>
        <p class="text-gray-400 mb-6">
          Вы уже активировали пробный период ранее. Для продолжения использования платных функций
          оформите подписку.
        </p>
        <div class="flex justify-center gap-4">
          <button @click="$router.push('/dashboard')" class="btn-secondary">
            Вернуться в панель
          </button>
          <button @click="$router.push('/profile')" class="btn-primary">Оформить подписку</button>
        </div>
      </div>

      <!-- Trial activation form -->
      <form v-else-if="!loading" @submit.prevent="activateTrial" class="space-y-8">
        <!-- Step 1: Choose Plan -->
        <div class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold text-sm"
              >
                1
              </div>
              <h2 class="text-lg font-semibold text-white font-display">Выберите тариф</h2>
            </div>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                v-for="plan in plans"
                :key="plan.slug"
                type="button"
                @click="form.plan_slug = plan.slug"
                :class="[
                  'p-5 rounded-xl border-2 text-left transition-all',
                  form.plan_slug === plan.slug
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-gray-700 hover:border-gray-600 bg-gray-800/50',
                ]"
              >
                <div class="flex items-center justify-between mb-3">
                  <span class="font-bold text-white text-lg">{{ plan.name }}</span>
                  <div
                    v-if="form.plan_slug === plan.slug"
                    class="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center"
                  >
                    <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <div class="text-2xl font-bold text-gradient mb-2">
                  {{ formatPrice(plan.price_monthly) }}
                  <span class="text-sm text-gray-400 font-normal">/мес</span>
                </div>
                <div class="text-sm text-gray-400 space-y-1">
                  <div>{{ formatNumber(plan.connections_limit) }} соединений</div>
                  <div>{{ formatNumber(plan.messages_limit) }} сообщений</div>
                </div>
              </button>
            </div>
            <div v-if="errors.plan_slug" class="mt-3 text-sm text-red-400">
              {{ errors.plan_slug }}
            </div>
          </div>
        </div>

        <!-- Step 2: Company Info -->
        <div class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold text-sm"
              >
                2
              </div>
              <h2 class="text-lg font-semibold text-white font-display">О вашей компании</h2>
            </div>
          </div>
          <div class="p-6 space-y-5">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="form-label">Название компании <span class="text-red-400">*</span></label>
                <input
                  v-model="form.company_name"
                  type="text"
                  class="input-field"
                  placeholder="ООО «Компания»"
                  required
                />
                <div v-if="errors.company_name" class="mt-1 text-sm text-red-400">
                  {{ errors.company_name }}
                </div>
              </div>

              <div>
                <label class="form-label">Размер компании <span class="text-red-400">*</span></label>
                <select v-model="form.company_size" class="input-field" required>
                  <option value="">Выберите...</option>
                  <option v-for="(label, value) in options.company_sizes" :key="value" :value="value">
                    {{ label }}
                  </option>
                </select>
                <div v-if="errors.company_size" class="mt-1 text-sm text-red-400">
                  {{ errors.company_size }}
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="form-label">Отрасль <span class="text-red-400">*</span></label>
                <select v-model="form.industry" class="input-field" required>
                  <option value="">Выберите...</option>
                  <option v-for="(label, value) in options.industries" :key="value" :value="value">
                    {{ label }}
                  </option>
                </select>
                <div v-if="errors.industry" class="mt-1 text-sm text-red-400">
                  {{ errors.industry }}
                </div>
              </div>

              <div>
                <label class="form-label">Ваша должность <span class="text-red-400">*</span></label>
                <select v-model="form.job_title" class="input-field" required>
                  <option value="">Выберите...</option>
                  <option v-for="(label, value) in options.job_titles" :key="value" :value="value">
                    {{ label }}
                  </option>
                </select>
                <div v-if="errors.job_title" class="mt-1 text-sm text-red-400">
                  {{ errors.job_title }}
                </div>
              </div>
            </div>

            <div>
              <label class="form-label">Телефон <span class="text-gray-500">(необязательно)</span></label>
              <input
                v-model="form.phone"
                type="tel"
                class="input-field"
                placeholder="+7 (999) 123-45-67"
              />
            </div>
          </div>
        </div>

        <!-- Step 3: Use Case -->
        <div class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold text-sm"
              >
                3
              </div>
              <h2 class="text-lg font-semibold text-white font-display">Как планируете использовать</h2>
            </div>
          </div>
          <div class="p-6 space-y-5">
            <div>
              <label class="form-label">Сценарий использования <span class="text-red-400">*</span></label>
              <select v-model="form.use_case" class="input-field" required>
                <option value="">Выберите...</option>
                <option v-for="(label, value) in options.use_cases" :key="value" :value="value">
                  {{ label }}
                </option>
              </select>
              <div v-if="errors.use_case" class="mt-1 text-sm text-red-400">
                {{ errors.use_case }}
              </div>
            </div>

            <div>
              <label class="form-label"
                >Расскажите подробнее <span class="text-gray-500">(необязательно)</span></label
              >
              <textarea
                v-model="form.use_case_description"
                class="input-field"
                rows="3"
                placeholder="Опишите ваш проект и как вы планируете использовать WebSocket..."
              ></textarea>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="form-label"
                  >Ожидаемое количество соединений
                  <span class="text-gray-500">(необязательно)</span></label
                >
                <select v-model="form.expected_connections" class="input-field">
                  <option value="">Выберите...</option>
                  <option
                    v-for="(label, value) in options.expected_connections"
                    :key="value"
                    :value="value"
                  >
                    {{ label }}
                  </option>
                </select>
              </div>

              <div>
                <label class="form-label"
                  >Ожидаемый объём сообщений
                  <span class="text-gray-500">(необязательно)</span></label
                >
                <select v-model="form.expected_messages" class="input-field">
                  <option value="">Выберите...</option>
                  <option
                    v-for="(label, value) in options.expected_messages"
                    :key="value"
                    :value="value"
                  >
                    {{ label }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: How did you find us -->
        <div class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold text-sm"
              >
                4
              </div>
              <h2 class="text-lg font-semibold text-white font-display">Откуда узнали о нас</h2>
            </div>
          </div>
          <div class="p-6">
            <div>
              <label class="form-label">Источник <span class="text-gray-500">(необязательно)</span></label>
              <select v-model="form.referral_source" class="input-field">
                <option value="">Выберите...</option>
                <option
                  v-for="(label, value) in options.referral_sources"
                  :key="value"
                  :value="value"
                >
                  {{ label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Error message -->
        <div v-if="errorMessage" class="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <p class="text-sm text-red-400">{{ errorMessage }}</p>
        </div>

        <!-- Submit -->
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            type="button"
            @click="$router.push('/dashboard')"
            class="btn-secondary w-full sm:w-auto"
          >
            Отмена
          </button>
          <button
            type="submit"
            :disabled="submitting || !form.plan_slug"
            class="btn-primary w-full sm:w-auto py-3 px-8 text-lg font-semibold flex items-center justify-center gap-2"
          >
            <svg
              v-if="submitting"
              class="w-5 h-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {{ submitting ? 'Активация...' : 'Активировать 14 дней бесплатно' }}
          </button>
        </div>
      </form>

      <!-- Loading state -->
      <div v-if="loading" class="card p-12 flex items-center justify-center">
        <div class="text-center">
          <svg class="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p class="text-gray-400">Загрузка...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useNotifications } from '../composables/useNotifications';
import api from '../services/api';

export default {
  name: 'ActivateTrial',
  setup() {
    const { success, error } = useNotifications();
    return { success, error };
  },
  data() {
    return {
      loading: true,
      submitting: false,
      canActivate: true,
      trialDays: 14,
      plans: [],
      options: {
        company_sizes: {},
        industries: {},
        job_titles: {},
        use_cases: {},
        referral_sources: {},
        expected_connections: {},
        expected_messages: {},
      },
      form: {
        plan_slug: '',
        company_name: '',
        company_size: '',
        industry: '',
        job_title: '',
        phone: '',
        use_case: '',
        use_case_description: '',
        expected_connections: '',
        expected_messages: '',
        referral_source: '',
      },
      errors: {},
      errorMessage: '',
    };
  },
  mounted() {
    this.loadOptions();
  },
  methods: {
    async loadOptions() {
      try {
        const response = await api.get('/trial/options');
        if (response.data.success) {
          const data = response.data.data;
          this.canActivate = data.can_activate;
          this.trialDays = data.trial_days;
          this.plans = data.plans;
          this.options = data.options;

          // Предвыбрать план из query параметра
          const planSlug = this.$route.query.plan;
          if (planSlug && this.plans.find((p) => p.slug === planSlug)) {
            this.form.plan_slug = planSlug;
          }
        }
      } catch (err) {
        this.error('Ошибка', 'Не удалось загрузить данные');
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async activateTrial() {
      this.errors = {};
      this.errorMessage = '';

      // Валидация
      if (!this.form.plan_slug) {
        this.errors.plan_slug = 'Выберите тариф';
        return;
      }
      if (!this.form.company_name) {
        this.errors.company_name = 'Введите название компании';
        return;
      }
      if (!this.form.company_size) {
        this.errors.company_size = 'Выберите размер компании';
        return;
      }
      if (!this.form.industry) {
        this.errors.industry = 'Выберите отрасль';
        return;
      }
      if (!this.form.job_title) {
        this.errors.job_title = 'Выберите должность';
        return;
      }
      if (!this.form.use_case) {
        this.errors.use_case = 'Выберите сценарий использования';
        return;
      }

      try {
        this.submitting = true;
        const response = await api.post('/trial/activate', this.form);

        if (response.data.success) {
          this.success('Успех!', response.data.message);
          this.$router.push('/dashboard');
        }
      } catch (err) {
        if (err.response?.data?.errors) {
          this.errors = err.response.data.errors;
        }
        this.errorMessage = err.response?.data?.message || 'Произошла ошибка при активации';
        this.error('Ошибка', this.errorMessage);
      } finally {
        this.submitting = false;
      }
    },

    formatPrice(price) {
      return new Intl.NumberFormat('ru-RU').format(price) + ' ₽';
    },

    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(0) + 'M';
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(0) + 'K';
      }
      return num.toString();
    },
  },
};
</script>
